import { Link } from 'gatsby';
import { forwardRef } from 'react';
import { ThemeUIStyleObject } from 'theme-ui';
import CollectionLink from '~/components/Collection/Link';
import ProductLink from '~/components/Product/Link';
import { useCustomAnalytics } from '~/hooks/utils/customAnalytics';
type Props = {
  title?: string;
  children: React.ReactNode;
  type?: string;
  linkType?: string;
  slug: string;
  href?: string;
  innerLink?: string;
  sx?: ThemeUIStyleObject;
  locationDetail?: string;
  page?: Queries.ContentfulPage;
  collectionSlug?: string;
  onClick?: () => void;
  [key: string]: any;
};

const LinkRenderer = forwardRef<HTMLElement, Props>(
  (
    {
      title,
      children,
      type,
      linkType,
      collectionSlug,
      /**
       * Link to a page in the site, ususally for pages
       * that are easily set in contentful CTA like products
       * or prebuilt surface pages
       */
      slug,
      /**
       * External link to a page outside of the site, or email
       */
      href,
      /**
       * Link to a page in the site usually for pages that are not
       * easily set in contentful `/blog/something/another`
       */
      innerLink,
      sx,
      locationDetail,
      page,
      onClick,
      ...props
    },
    ref
  ) => {
    const { trackClickLink } = useCustomAnalytics();

    if (/collection/i.test(type ?? '')) {
      return (
        <CollectionLink locationDetail={locationDetail} title={title} slug={slug} sx={sx} {...props}>
          {children}
        </CollectionLink>
      );
    }
    if (/product/i.test(type ?? '')) {
      return (
        <ProductLink
          productName={title ?? ''}
          productSlug={slug}
          collectionSlug={collectionSlug}
          locationDetail={locationDetail}
          sx={sx}
          {...props}
        >
          {children}
        </ProductLink>
      );
    }

    if (/activity/i.test(page?.type ?? '')) {
      return (
        // @ts-ignore - need to update to react 18 to fix this
        <Link
          to={`/activities/${slug}/`}
          onClick={() => trackClickLink({ type, title, locationDetail })}
          sx={{
            ...sx,
          }}
          ref={ref}
          aria-label={`Navigate to ${title}`}
          {...props}
        >
          {/* @ts-ignore - need to update to react 18 to fix this */}
          {children}
        </Link>
      );
    }

    if (href) {
      return (
        <a
          onClick={() => trackClickLink({ type, title, locationDetail })}
          href={href}
          sx={{
            ...sx,
          }}
          {...props}
        >
          {children}
        </a>
      );
    }

    const slugOrInnerLink = slug ?? innerLink; // slug takes precedence over innerLink

    const startsWithSlash = slugOrInnerLink?.charAt(0) === '/';
    const isHome = slugOrInnerLink === 'home' || slugOrInnerLink === '/home';

    const linkHasId = slugOrInnerLink?.indexOf('#') !== -1 && !!slugOrInnerLink;
    const getLinkPath = () => {
      if (isHome || !slugOrInnerLink) return '/';
      const formattedSlug = startsWithSlash ? slugOrInnerLink : `/${slugOrInnerLink}`;
      return linkHasId ? formattedSlug : `${formattedSlug}/`;
    };
    const handleClick = () => {
      trackClickLink({ linkType, title, locationDetail });
      onClick?.();
    };
    return (
      // @ts-ignore - need to update to react 18 to fix this
      <Link
        ref={ref}
        to={getLinkPath()}
        state={{ disableScrollUpdate: linkHasId }}
        onClick={handleClick}
        sx={{
          ...sx,
        }}
        aria-label={title ? `Navigate to ${title}` : undefined}
        {...props}
      >
        {/* @ts-ignore - need to update to react 18 to fix this */}
        {children}
      </Link>
    );
  }
);

export default LinkRenderer;
